import {
    Close as CloseIcon,
    LocationOn as LocationIcon,
    MyLocation as MyLocationIcon,
    Search as SearchIcon
} from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    TextField,
    Typography
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';

// Google Maps loading utility
let isGoogleMapsLoaded = false;
let isGoogleMapsLoading = false;
let googleMapsLoadPromise = null;

const loadGoogleMaps = () => {
    if (isGoogleMapsLoaded && window.google?.maps?.Map) {
        return Promise.resolve();
    }

    if (isGoogleMapsLoading && googleMapsLoadPromise) {
        return googleMapsLoadPromise;
    }

    isGoogleMapsLoading = true;

    googleMapsLoadPromise = new Promise((resolve, reject) => {
        const existingScripts = document.querySelectorAll('script[src*="maps.googleapis.com"]');
        existingScripts.forEach(script => script.remove());

        if (window.initMap) delete window.initMap;
        if (window.initGoogleMaps) delete window.initGoogleMaps;

        const callbackName = `initGoogleMaps_${Date.now()}`;

        window[callbackName] = () => {
            isGoogleMapsLoaded = true;
            isGoogleMapsLoading = false;
            setTimeout(() => {
                if (window[callbackName]) {
                    delete window[callbackName];
                }
            }, 1000);
            resolve();
        };

        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places&language=ar&region=IL&callback=${callbackName}`;
        script.async = true;
        script.defer = true;

        script.onerror = () => {
            isGoogleMapsLoading = false;
            googleMapsLoadPromise = null;
            reject(new Error('Failed to load Google Maps'));
        };

        document.head.appendChild(script);
    });

    return googleMapsLoadPromise;
};

const MapAddressDialog = ({
    open,
    onClose,
    onAddressSelect,
    initialAddress = '',
    initialCoordinates = null,
    initialDisplayName = ''
}) => {
    const mapRef = useRef(null);
    const [marker, setMarker] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedAddress, setSelectedAddress] = useState(initialAddress);
    const [selectedDisplayName, setSelectedDisplayName] = useState(initialDisplayName);
    const [selectedCoordinates, setSelectedCoordinates] = useState(initialCoordinates);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [mapLoaded, setMapLoaded] = useState(false);
    const [mapReady, setMapReady] = useState(false);
    const [initKey, setInitKey] = useState(0);
    const markersRef = useRef([]);
    const mapInstanceRef = useRef(null);

    const clearAllMarkers = useCallback(() => {
        markersRef.current.forEach(m => {
            if (m && m.setMap) m.setMap(null);
        });
        markersRef.current = [];
        if (marker && marker.setMap) {
            marker.setMap(null);
            setMarker(null);
        }
    }, [marker]);

    useEffect(() => {
        if (open) {
            setSelectedAddress(initialAddress);
            setSelectedDisplayName(initialDisplayName);
            setSelectedCoordinates(initialCoordinates);
            setError('');
            setSearchQuery('');
            setMapReady(false);
            setMapLoaded(false);
            mapInstanceRef.current = null;
            setInitKey(prev => prev + 1);
        }
    }, [open, initialAddress, initialCoordinates, initialDisplayName]);

    const getAddressFromCoordinates = async (position) => {
        if (!window.google?.maps?.Geocoder) return;

        const geocoder = new window.google.maps.Geocoder();
        try {
            const result = await new Promise((resolve, reject) => {
                geocoder.geocode(
                    { location: position, language: 'ar', region: 'IL' },
                    (results, status) => {
                        if (status === 'OK' && results && results[0]) {
                            resolve(results[0]);
                        } else {
                            reject(new Error(`Geocoding failed: ${status}`));
                        }
                    }
                );
            });

            setSelectedAddress(result.formatted_address);

            const addressComponents = result.address_components;
            let displayName = '';

            const neighborhood = addressComponents.find(comp =>
                comp.types.includes('neighborhood') ||
                comp.types.includes('sublocality') ||
                comp.types.includes('sublocality_level_1')
            );
            const city = addressComponents.find(comp =>
                comp.types.includes('locality') ||
                comp.types.includes('administrative_area_level_2')
            );

            if (neighborhood && city) {
                displayName = `${neighborhood.long_name}، ${city.long_name}`;
            } else if (city) {
                displayName = city.long_name;
            } else if (neighborhood) {
                displayName = neighborhood.long_name;
            } else {
                const formattedParts = result.formatted_address.split('،').map(part => part.trim());
                const nonPlusCodeParts = formattedParts.filter(part =>
                    !part.match(/^[A-Z0-9]{4}\+[A-Z0-9]{2}/)
                );
                displayName = nonPlusCodeParts.slice(0, 2).join('، ');
            }

            if (!displayName || displayName.length < 3) {
                if (city) {
                    displayName = city.long_name;
                } else {
                    displayName = result.formatted_address.split('،')[1]?.trim() || result.formatted_address;
                }
            }

            setSelectedDisplayName(displayName);
        } catch (err) {
            const coordAddress = `${position.lat.toFixed(6)}, ${position.lng.toFixed(6)}`;
            setSelectedAddress(coordAddress);
            setSelectedDisplayName('موقع مخصص');
        }
    };

    const addMarkerToMap = useCallback((position, shouldGetAddress = true) => {
        const mapInstance = mapInstanceRef.current;
        if (!mapInstance || !window.google?.maps?.Marker) return;

        try {
            clearAllMarkers();

            const newMarker = new window.google.maps.Marker({
                position,
                map: mapInstance,
                animation: window.google.maps.Animation.DROP,
                icon: {
                    url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23ff4444" width="32" height="32">
                            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                        </svg>
                    `),
                    scaledSize: new window.google.maps.Size(32, 32),
                    anchor: new window.google.maps.Point(16, 32)
                }
            });

            setMarker(newMarker);
            markersRef.current.push(newMarker);
            setSelectedCoordinates({ lat: position.lat, lng: position.lng });

            if (shouldGetAddress) {
                getAddressFromCoordinates(position);
            }
        } catch (err) {
            setError("فشل في إضافة علامة الموقع. يرجى المحاولة مرة أخرى.");
        }
    }, [clearAllMarkers]);

    const setupMap = useCallback(() => {
        if (!window.google?.maps?.Map || !mapRef.current || mapInstanceRef.current) return;

        try {
            mapRef.current.innerHTML = '';
            const defaultCenter = selectedCoordinates || initialCoordinates || { lat: 32.8651272, lng: 35.2857841 };

            const mapInstance = new window.google.maps.Map(mapRef.current, {
                center: defaultCenter,
                zoom: (selectedCoordinates || initialCoordinates) ? 15 : 11,
                mapTypeControl: true,
                mapTypeControlOptions: {
                    style: window.google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
                    position: window.google.maps.ControlPosition.TOP_RIGHT,
                    mapTypeIds: [
                        window.google.maps.MapTypeId.ROADMAP,
                        window.google.maps.MapTypeId.SATELLITE,
                        window.google.maps.MapTypeId.HYBRID
                    ]
                },
                streetViewControl: false,
                fullscreenControl: false,
                gestureHandling: 'greedy',
                disableDefaultUI: false,
                clickableIcons: true,
                styles: [
                    { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] }
                ]
            });

            mapInstanceRef.current = mapInstance;

            const readyPromise = new Promise((resolve) => {
                let resolved = false;
                const resolveOnce = () => { if (!resolved) { resolved = true; resolve(); } };
                window.google.maps.event.addListenerOnce(mapInstance, 'tilesloaded', resolveOnce);
                window.google.maps.event.addListenerOnce(mapInstance, 'idle', resolveOnce);
                setTimeout(resolveOnce, 2000);
            });

            readyPromise.then(() => {
                mapInstance.addListener('click', (event) => {
                    const position = { lat: event.latLng.lat(), lng: event.latLng.lng() };
                    addMarkerToMap(position, true);
                });

                if (selectedCoordinates || initialCoordinates) {
                    addMarkerToMap(defaultCenter, false);
                }
                setMapReady(true);
            });
        } catch (err) {
            setError('فشل في إعداد الخريطة. يرجى المحاولة مرة أخرى.');
        }
    }, [selectedCoordinates, initialCoordinates, addMarkerToMap]);

    const initializeGoogleMaps = useCallback(async () => {
        try {
            setMapLoaded(false);
            setError('');
            await loadGoogleMaps();
            setMapLoaded(true);
            setTimeout(() => {
                if (mapRef.current && window.google?.maps?.Map) {
                    setupMap();
                } else {
                    setError('خطأ في تحميل الخريطة. يرجى المحاولة مرة أخرى.');
                }
            }, 100);
        } catch (err) {
            setError('فشل في تحميل خرائط جوجل. يرجى التأكد من الاتصال بالإنترنت ومفتاح API.');
            setMapLoaded(true);
        }
    }, [setupMap]);

    useEffect(() => {
        if (open) initializeGoogleMaps();
    }, [open, initKey, initializeGoogleMaps]);

    useEffect(() => {
        if (!open) {
            clearAllMarkers();
            if (mapInstanceRef.current && window.google?.maps?.event) {
                window.google.maps.event.clearInstanceListeners(mapInstanceRef.current);
            }
            mapInstanceRef.current = null;
            setMapReady(false);
            setMapLoaded(false);
            setError('');
        }
    }, [open, clearAllMarkers]);

    const handleSearch = async () => {
        if (!searchQuery.trim() || !window.google?.maps?.Geocoder) return;

        setLoading(true);
        setError('');

        try {
            const geocoder = new window.google.maps.Geocoder();
            const result = await new Promise((resolve, reject) => {
                geocoder.geocode(
                    { address: searchQuery, region: 'IL', language: 'ar' },
                    (results, status) => {
                        if (status === 'OK' && results && results[0]) {
                            resolve(results[0]);
                        } else {
                            reject(new Error('لم يتم العثور على العنوان المطلوب'));
                        }
                    }
                );
            });

            const location = result.geometry.location;
            const position = { lat: location.lat(), lng: location.lng() };

            const mapInstance = mapInstanceRef.current;
            if (mapInstance) {
                mapInstance.setCenter(position);
                mapInstance.setZoom(15);
                addMarkerToMap(position, true);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleGetCurrentLocation = () => {
        if (!navigator.geolocation) {
            setError('الموقع الجغرافي غير مدعوم في هذا المتصفح');
            return;
        }

        setLoading(true);
        setError('');

        navigator.geolocation.getCurrentPosition(
            (position) => {
                const currentLocation = { lat: position.coords.latitude, lng: position.coords.longitude };
                const mapInstance = mapInstanceRef.current;
                if (mapInstance) {
                    mapInstance.setCenter(currentLocation);
                    mapInstance.setZoom(15);
                    addMarkerToMap(currentLocation, true);
                }
                setLoading(false);
            },
            () => {
                setError('فشل في الحصول على الموقع الحالي');
                setLoading(false);
            },
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
        );
    };

    const handleConfirm = () => {
        if (selectedAddress && selectedCoordinates) {
            onAddressSelect({
                address: selectedAddress,
                displayName: selectedDisplayName || selectedAddress,
                coordinates: selectedCoordinates
            });
            onClose();
        }
    };

    const handleClose = () => {
        setError('');
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{ sx: { height: '80vh', maxHeight: 600 } }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationIcon color="primary" />
                    <Typography variant="h6">اختر موقع العقار</Typography>
                </Box>
                <IconButton onClick={handleClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="ابحث عن عنوان أو منطقة..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                            InputProps={{
                                endAdornment: (
                                    <IconButton size="small" onClick={handleSearch} disabled={loading || !searchQuery.trim()}>
                                        {loading ? <CircularProgress size={20} /> : <SearchIcon />}
                                    </IconButton>
                                )
                            }}
                        />
                        <Button
                            variant="outlined"
                            startIcon={<MyLocationIcon />}
                            onClick={handleGetCurrentLocation}
                            disabled={loading}
                            sx={{ minWidth: 'auto', px: 2 }}
                        >
                            موقعي
                        </Button>
                    </Box>

                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
                    )}

                    {selectedAddress && (
                        <Box sx={{ bgcolor: 'primary.light', color: 'primary.contrastText', p: 2, borderRadius: 1, fontSize: '0.875rem' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                                العنوان المحدد: {selectedAddress}
                            </Typography>
                            <TextField
                                fullWidth
                                size="small"
                                label="اسم الموقع للعرض (اختياري)"
                                value={selectedDisplayName}
                                onChange={(e) => setSelectedDisplayName(e.target.value)}
                                placeholder="مثال: حي الزهراء، حيفا"
                                helperText="يمكنك تخصيص اسم أكثر وضوحاً لعرض الموقع"
                                sx={{
                                    mt: 1,
                                    '& .MuiOutlinedInput-root': {
                                        bgcolor: 'rgba(255,255,255,0.1)',
                                        '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                                        '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
                                        '&.Mui-focused fieldset': { borderColor: 'rgba(255,255,255,0.7)' },
                                    },
                                    '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.9)' },
                                    '& .MuiFormHelperText-root': { color: 'rgba(255,255,255,0.7)' },
                                    '& .MuiInputBase-input': { color: 'white' }
                                }}
                            />
                        </Box>
                    )}
                </Box>

                <Box sx={{ flex: 1, position: 'relative' }}>
                    {(!mapLoaded || !mapReady) && (
                        <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
                            <CircularProgress sx={{ mb: 2 }} />
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                جاري تحميل الخريطة...
                            </Typography>
                            <Button size="small" variant="outlined" onClick={() => setInitKey(prev => prev + 1)}>
                                إعادة المحاولة
                            </Button>
                        </Box>
                    )}

                    <div
                        key={`map-${initKey}`}
                        ref={mapRef}
                        style={{ width: '100%', height: '100%', display: (mapLoaded && mapReady) ? 'block' : 'none' }}
                    />

                    {mapLoaded && mapReady && !selectedAddress && (
                        <Box sx={{ position: 'absolute', top: 16, left: 16, bgcolor: 'rgba(255, 255, 255, 0.9)', p: 2, borderRadius: 1, boxShadow: 2, maxWidth: 250 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>كيفية الاستخدام:</Typography>
                            <Typography variant="caption" color="text.secondary">
                                • انقر على الخريطة لاختيار الموقع<br />
                                • استخدم البحث للعثور على عنوان محدد<br />
                                • اضغط "موقعي" لاستخدام موقعك الحالي
                            </Typography>
                        </Box>
                    )}
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
                <Button onClick={handleClose} color="inherit">إلغاء</Button>
                {!mapReady && (
                    <Button onClick={() => setInitKey(prev => prev + 1)} color="primary" variant="outlined" size="small">
                        إعادة تحميل الخريطة
                    </Button>
                )}
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    disabled={!selectedAddress || !selectedCoordinates}
                    startIcon={<LocationIcon />}
                >
                    تأكيد الموقع
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default MapAddressDialog;
